import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Target, ArrowRight, Calendar, Building2, DollarSign, Clock,
  AlertCircle, Sparkles, Plus, Check, Loader2, FileText, Filter,
} from 'lucide-react';
import { loggedGet } from './loggedFetch';
import PoLink from '../PoDetailModal/PoLink';
import logger from '../../lib/logger';

/*
 * TakeoutView — Phase 4. Competitor renewals coming up in the next
 * 12-24 months, paired with a side-by-side of the target vendor's
 * counter-product so the user can see at a glance: what's renewing,
 * what we'd put in its place, how strong the fit is.
 *
 * Consumes /:vendor/renewals — competitor rows are enriched server-side
 * with recommended_counter_product / positioning_fit / positioning_strategy
 * via competitiveStrategy() + VENDOR_DOMAIN_GATE. The vocabulary matches
 * DisplacementTable / /displacement so both views speak the same shape.
 *
 * Each card has a single Add-to-plan button that seeds a kanban play
 * with both products in seed_payload — the briefing prompt then has
 * everything it needs to write the side-by-side narrative. Weak-fit
 * rows (where the target doesn't sell a direct equivalent) are still
 * shown for visibility but the Add button is disabled.
 */

const WINDOW_PRESETS = [
  { label: '12 months', days: 365 },
  { label: '18 months', days: 540 },
  { label: '24 months', days: 730 },
];

// Tones map to existing CSS variants (--tight | --loose | --unknown) so
// no new styles are needed; only the labels change vs. the legacy vocab.
const FIT_LABEL = {
  strong:   { label: 'Strong fit',   tone: 'tight'   },
  moderate: { label: 'Moderate fit', tone: 'loose'   },
  weak:     { label: 'Weak fit',     tone: 'unknown' },
  unknown:  { label: 'Unmapped',     tone: 'unknown' },
};

function formatCurrency(num) {
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

function formatDateShort(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function engageBadge(daysToEngage) {
  if (daysToEngage == null) return null;
  if (daysToEngage <= 0) {
    return { className: 'is-now', label: 'Engage now', icon: AlertCircle };
  }
  if (daysToEngage <= 60) {
    return { className: 'is-soon', label: `${daysToEngage}d to engage`, icon: Clock };
  }
  if (daysToEngage <= 180) {
    return { className: 'is-watch', label: `${daysToEngage}d to engage`, icon: Calendar };
  }
  return { className: 'is-future', label: `${daysToEngage}d to engage`, icon: Calendar };
}

function TakeoutView({ vendor, includeLowConfidence, strict, productFamily, departmentIds, listName, years, onSeedPlan }) {
  const [windowDays, setWindowDays] = useState(730);
  const [playFilter, setPlayFilter] = useState('all');
  const [fitFilter, setFitFilter] = useState('all');
  const [addedIds, setAddedIds] = useState({});

  const qs = new URLSearchParams();
  qs.set('windowDays', String(windowDays));
  if (includeLowConfidence) qs.set('includeLowConfidence', 'true');
  if (!strict) qs.set('strict', 'false');
  // Forward-compat: send the chip selection even though /renewals doesn't
  // filter by it yet (matview rebuild needed — see backend TODO).
  if (productFamily) qs.set('product_family', productFamily);
  // Phase H.29: department_ids filter for list scoping
  if (departmentIds?.length) qs.set('department_ids', departmentIds.join(','));
  // Global time filter from TimeFilterContext (null = all-time)
  if (years != null) qs.set('years', String(years));
  const path = `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/renewals?${qs}`;

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-analysis', 'renewals', vendor, windowDays, includeLowConfidence, strict, productFamily, departmentIds, years],
    queryFn: loggedGet('TakeoutView:fetch', path),
    enabled: !!vendor,
  });

  const allRows = data?.competitor || [];

  // Fit priority: strong=0, moderate=1, weak=2, unknown/unmapped=3
  // This ensures unmapped items appear last when sorted.
  const FIT_PRIORITY = { strong: 0, moderate: 1, weak: 2, unknown: 3 };

  const filtered = useMemo(() => {
    return allRows
      .filter(r => playFilter === 'all' ? true : r.play_type === playFilter)
      .filter(r => fitFilter === 'all' ? true : (r.positioning_fit || 'unknown') === fitFilter)
      .sort((a, b) => {
        // Primary sort: fit priority (strong → moderate → weak → unmapped)
        const aFit = FIT_PRIORITY[a.positioning_fit] ?? FIT_PRIORITY.unknown;
        const bFit = FIT_PRIORITY[b.positioning_fit] ?? FIT_PRIORITY.unknown;
        if (aFit !== bFit) return aFit - bFit;
        // Secondary sort: days to engage (urgent first)
        const aD = a.days_to_engage ?? 9999;
        const bD = b.days_to_engage ?? 9999;
        if (aD !== bD) return aD - bD;
        // Tertiary sort: contract value (largest first)
        return (b.contract_value || 0) - (a.contract_value || 0);
      });
  }, [allRows, playFilter, fitFilter]);

  const fitCounts = useMemo(() => {
    const m = { all: allRows.length, strong: 0, moderate: 0, weak: 0, unknown: 0 };
    for (const r of allRows) {
      const fit = r.positioning_fit || 'unknown';
      if (m[fit] !== undefined) m[fit] += 1;
      else m.unknown += 1;
    }
    return m;
  }, [allRows]);

  const playCounts = useMemo(() => {
    const m = { all: allRows.length, offensive: 0, coexist: 0 };
    for (const r of allRows) {
      if (r.play_type === 'offensive') m.offensive += 1;
      else if (r.play_type === 'coexist') m.coexist += 1;
    }
    return m;
  }, [allRows]);

  const totals = useMemo(() => {
    const v = filtered.reduce((s, r) => s + (r.contract_value || 0), 0);
    return { count: filtered.length, value: v };
  }, [filtered]);

  const handleSeedTakeout = (row) => {
    if (!onSeedPlan) return;
    const days = row.days_to_engage ?? row.days_to_end ?? 0;
    const colKey = days <= 30 ? '30' : days <= 60 ? '60' : days <= 90 ? '90' : 'backlog';
    const playLabel = row.play_type === 'offensive' ? 'Offensive displace' : 'Coexist / EA wrap';
    const counter = row.recommended_counter_product;
    const fitKey = row.positioning_fit || 'unknown';
    onSeedPlan({
      kind: 'takeout',
      column_key: colKey,
      title: `Take out ${row.competitor} ${row.competitor_product || row.category} at ${row.agency}` +
             (counter ? ` → ${counter}` : ''),
      rationale: [
        `${row.competitor}${row.competitor_product ? ` (${row.competitor_product})` : ''} contract at ${row.agency} ends ${formatDateShort(row.end_date)} — ${formatCurrency(row.contract_value)} addressable.`,
        row.days_to_engage <= 0
          ? 'Engage now — already inside the budget cycle.'
          : `Engage by: ${row.days_to_engage} days from today.`,
        counter
          ? `Counter-product: ${counter} (${FIT_LABEL[fitKey].label}).`
          : 'No specific counter-product mapped — confirm SKU before approach.',
        row.positioning_strategy ? `Positioning: ${row.positioning_strategy}` : '',
        row.acquisition_method ? `Procurement vehicle: ${row.acquisition_method}.` : '',
        `Play: ${playLabel}.`,
      ].filter(Boolean).join(' '),
      dollar_impact: row.contract_value,
      due_date: row.end_date,
      department_id: row.department_id,
      agency: row.agency,
      competitor: row.competitor,
      competitor_product: row.competitor_product,
      recommended_counter_product: counter,
      positioning_fit: row.positioning_fit,
      positioning_strategy: row.positioning_strategy,
      play_type: row.play_type,
      contract_value: row.contract_value,
      line_count: row.line_count,
      end_date: row.end_date,
      engage_by_days: row.days_to_engage,
      sample_description: row.sample_description,
      purchase_document_num: row.purchase_document_num,
    });
    setAddedIds(prev => ({
      ...prev,
      [`${row.purchase_document_num}-${row.department_id}`]: true,
    }));
    logger.vendorAnalysis.info('TakeoutView:seed-plan', {
      vendor, competitor: row.competitor, agency: row.agency, positioning_fit: fitKey,
    });
  };

  return (
    <div className="vca-section vca-takeout">
      <div className="vca-section-head">
        <div>
          <h2>
            <Target size={16} style={{ verticalAlign: 'text-bottom' }} /> Take-out opportunities · 12-24 month window
          </h2>
          <p className="vca-section__sub">
            Competitor renewals in the next {windowDays === 365 ? '12' : windowDays === 540 ? '18' : '24'} months
            paired with a side-by-side of {vendor}'s counter-product. The fit
            badge tells you how strong the displacement story is. Each card
            is one-click into the 30-60-90 plan with both products carried
            into the seed payload so the Master-Architect briefing has the
            full positioning context.
          </p>
        </div>
        <div className="vca-takeout-window">
          {WINDOW_PRESETS.map(w => (
            <button
              key={w.days}
              type="button"
              className={`vca-add-btn vca-takeout-window-btn ${windowDays === w.days ? 'is-active' : ''}`}
              onClick={() => setWindowDays(w.days)}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <div className="vca-renewal-filters">
        <div className="vca-renewal-filter-group">
          <span className="vca-renewal-filter-label">
            <Filter size={11} /> Play
          </span>
          <button type="button"
            className={`vca-renewal-size-chip ${playFilter === 'all' ? 'is-active' : ''}`}
            onClick={() => setPlayFilter('all')}>
            <span>All</span><span className="vca-renewal-size-chip-count">{playCounts.all}</span>
          </button>
          <button type="button"
            className={`vca-renewal-size-chip ${playFilter === 'offensive' ? 'is-active' : ''}`}
            onClick={() => setPlayFilter('offensive')}>
            <span>Offensive</span><span className="vca-renewal-size-chip-count">{playCounts.offensive}</span>
          </button>
          <button type="button"
            className={`vca-renewal-size-chip ${playFilter === 'coexist' ? 'is-active' : ''}`}
            onClick={() => setPlayFilter('coexist')}>
            <span>Coexist</span><span className="vca-renewal-size-chip-count">{playCounts.coexist}</span>
          </button>
        </div>

        <div className="vca-renewal-filter-group">
          <span className="vca-renewal-filter-label">Fit</span>
          <button type="button"
            className={`vca-renewal-size-chip ${fitFilter === 'all' ? 'is-active' : ''}`}
            onClick={() => setFitFilter('all')}>
            <span>All</span><span className="vca-renewal-size-chip-count">{fitCounts.all}</span>
          </button>
          <button type="button"
            className={`vca-renewal-size-chip ${fitFilter === 'strong' ? 'is-active' : ''}`}
            onClick={() => setFitFilter('strong')}>
            <span>Strong</span><span className="vca-renewal-size-chip-count">{fitCounts.strong}</span>
          </button>
          <button type="button"
            className={`vca-renewal-size-chip ${fitFilter === 'moderate' ? 'is-active' : ''}`}
            onClick={() => setFitFilter('moderate')}>
            <span>Moderate</span><span className="vca-renewal-size-chip-count">{fitCounts.moderate}</span>
          </button>
          <button type="button"
            className={`vca-renewal-size-chip ${fitFilter === 'weak' ? 'is-active' : ''}`}
            onClick={() => setFitFilter('weak')}
            title="Outside the target vendor's product domain — no real displacement story">
            <span>Weak</span><span className="vca-renewal-size-chip-count">{fitCounts.weak}</span>
          </button>
          <button type="button"
            className={`vca-renewal-size-chip ${fitFilter === 'unknown' ? 'is-active' : ''}`}
            onClick={() => setFitFilter('unknown')}>
            <span>Unmapped</span><span className="vca-renewal-size-chip-count">{fitCounts.unknown}</span>
          </button>
        </div>

        <div style={{ marginLeft: 'auto', fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
          <strong>{totals.count}</strong> opportunit{totals.count === 1 ? 'y' : 'ies'} ·
          {' '}<strong>{formatCurrency(totals.value)}</strong> addressable
        </div>
      </div>

      {isLoading && (
        <div className="vca-section__loading">
          <Loader2 size={14} className="vca-spin" style={{ verticalAlign: 'middle' }} /> Loading take-out opportunities…
        </div>
      )}

      {error && (
        <div className="vca-section__error">Renewals failed: {error.message}</div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <div className="vca-section__sub" style={{ padding: '16px 0' }}>
          {allRows.length === 0
            ? `No competitor renewals in the next ${windowDays === 365 ? '12' : windowDays === 540 ? '18' : '24'} months — try expanding the window.`
            : 'No opportunities match the current filters. Try clearing fit / play.'}
        </div>
      )}

      {!isLoading && !error && filtered.length > 0 && (
        <div className="vca-takeout-grid">
          {filtered.map(row => {
            const key = `${row.purchase_document_num}-${row.department_id}`;
            const added = !!addedIds[key];
            const eng = engageBadge(row.days_to_engage);
            const fitKey = row.positioning_fit || 'unknown';
            const fit = FIT_LABEL[fitKey] || FIT_LABEL.unknown;
            const counter = row.recommended_counter_product;
            const EngIcon = eng?.icon || Calendar;
            // Mirror DisplacementTable: weak fit means the target doesn't
            // sell a real equivalent (per VENDOR_DOMAIN_GATE on the server),
            // so the Add-to-plan path would seed a misleading play.
            const addDisabled = added || fitKey === 'weak';

            return (
              <article key={key} className={`vca-takeout-card vca-takeout-card--${fit.tone}`}>
                <header className="vca-takeout-card-head">
                  <div className="vca-takeout-card-title-row">
                    {eng && (
                      <span className={`vca-takeout-engage ${eng.className}`}>
                        <EngIcon size={11} /> {eng.label}
                      </span>
                    )}
                    <span
                      className={`vca-takeout-fit vca-takeout-fit--${fit.tone}`}
                      title="Curated: Fit score from hand-maintained competitive positioning library"
                    >
                      {fit.label}
                    </span>
                    <span className={`vca-takeout-play vca-takeout-play--${row.play_type}`}>
                      {row.play_type === 'offensive' ? 'Offensive' : 'Coexist'}
                    </span>
                  </div>
                  <div className="vca-takeout-agency">
                    <Building2 size={13} />
                    <span>{row.agency}</span>
                  </div>
                </header>

                <div className="vca-takeout-vs">
                  <div className="vca-takeout-side vca-takeout-side--competitor">
                    <div className="vca-takeout-side-label">Renewing</div>
                    <div className="vca-takeout-side-vendor">{row.competitor}</div>
                    <div className="vca-takeout-side-product">
                      {row.competitor_product || row.top_component || row.category || 'Unspecified product'}
                    </div>
                    <div className="vca-takeout-side-meta">
                      <div>
                        <span className="vca-takeout-side-meta-label"><DollarSign size={10} /> Contract</span>
                        <span className="vca-takeout-side-meta-val">{formatCurrency(row.contract_value)}</span>
                      </div>
                      <div>
                        <span className="vca-takeout-side-meta-label"><Calendar size={10} /> Ends</span>
                        <span className="vca-takeout-side-meta-val">{formatDateShort(row.end_date)}</span>
                      </div>
                    </div>
                    {row.sample_description && (
                      <div className="vca-takeout-side-desc" title={row.sample_description}>
                        "{row.sample_description.slice(0, 140)}{row.sample_description.length > 140 ? '…' : ''}"
                      </div>
                    )}
                  </div>

                  <div className="vca-takeout-arrow" aria-hidden="true">
                    <ArrowRight size={20} />
                    <span className="vca-takeout-arrow-label">vs</span>
                  </div>

                  <div className="vca-takeout-side vca-takeout-side--target">
                    <div className="vca-takeout-side-label">Counter-product</div>
                    <div className="vca-takeout-side-vendor">{vendor}</div>
                    <div className="vca-takeout-side-product">
                      {counter || (
                        <span className="vca-takeout-side-product-na">
                          No specific SKU mapped yet — confirm before approach
                        </span>
                      )}
                    </div>
                    {row.positioning_strategy && (
                      <div
                        className="vca-takeout-side-strategy"
                        title="Curated: Positioning guidance from hand-maintained competitive strategy library"
                      >
                        <Sparkles size={11} style={{ verticalAlign: 'middle' }} /> {row.positioning_strategy}
                      </div>
                    )}
                  </div>
                </div>

                <footer className="vca-takeout-card-foot">
                  {row.purchase_document_num && (
                    <span className="vca-takeout-po">
                      <FileText size={11} /> PO{' '}
                      <PoLink
                        documentNumber={row.purchase_document_num}
                        departmentId={row.department_id}
                        startDate={row.earliest_start_date}
                        title={`Open PO ${row.purchase_document_num} — verify what this contract is actually for`}
                      />
                      {row.line_count ? ` · ${row.line_count} line${row.line_count === 1 ? '' : 's'}` : ''}
                    </span>
                  )}
                  {onSeedPlan && (
                    <button
                      className="vca-add-btn"
                      onClick={() => handleSeedTakeout(row)}
                      disabled={addDisabled}
                      title={
                        fitKey === 'weak'
                          ? `${vendor} does not sell a direct equivalent — not a real displacement target`
                          : 'Seed a play in the 30-60-90 plan with both products and the engage-by date'
                      }
                    >
                      {added ? <Check size={13} /> : <Plus size={13} />}
                      <span>{added ? 'Added to plan' : 'Add to plan'}</span>
                    </button>
                  )}
                </footer>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TakeoutView;
